import { Fragment, useEffect, useState } from "react";
import Sidebar from "../Dashboard/Sidebar";
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from "react-router-dom";
import { getpatient, updatePatient } from "../../actions/patientActions";
import { clearError, clearPatientUpdated } from "../../slices/patientSlice";
import { toast } from "react-toastify";
import Loader from "../layouts/Loader";
import calculateAgeFromDOB from "../../utils/calculateAge";
import AppDatePicker from "../common/AppDatePicker";

export default function UpdatePatient() {
    const [name, setName] = useState("");
    const [dob, setDob] = useState("");
    const [gender, setGender] = useState("");
    const [phone, setPhone] = useState("");
    const [address, setAddress] = useState("");
    const [remarks, setRemarks] = useState("");
    const [images, setImages] = useState([]);
    const [imagesCleared, setImagesCleared] = useState(false);
    const [imagesPreview, setImagesPreview] = useState([]);
    const { id: patientId } = useParams();

    const calculatedAge = calculateAgeFromDOB(dob);

    const { loading, isPatientUpdated, error, patient } = useSelector(state => state.patientState)

    const genders = [
        'Male',
        'Female'
    ];
    const dispatch = useDispatch();

    const onImagesChange = (e) => {
        const files = Array.from(e.target.files);

        files.forEach(file => {

            const reader = new FileReader();

            reader.onload = () => {
                if (reader.readyState === 2) {
                    setImagesPreview(oldArray => [...oldArray, reader.result])
                    setImages(oldArray => [...oldArray, file])
                }
            }

            reader.readAsDataURL(file)
        })
    }

    const submitHandler = (e) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append('name', name);
        formData.append('phone', phone);
        formData.append('dob', dob);
        formData.append('age', calculatedAge !== "" ? calculatedAge : (patient?.age || 0));
        formData.append('gender', gender);
        formData.append('address', address ? address.trim().toUpperCase() : '');
        formData.append('remarks', remarks);
        formData.append('doa', patient?.doa || '');
        formData.append('category', patient?.category || 'Out Patient');
        images.forEach(image => {
            formData.append('images', image)
        })
        formData.append('imagesCleared', imagesCleared);
        dispatch(updatePatient(patientId, formData))
    }

    const clearImagesHandler = () => {
        setImages([]);
        setImagesPreview([]);
        setImagesCleared(true);
    }


    useEffect(() => {
        dispatch(clearPatientUpdated());
        dispatch(clearError());
        return () => {
            dispatch(clearPatientUpdated());
            dispatch(clearError());
        };
    }, [dispatch]);

    useEffect(() => {
        if (isPatientUpdated) {
            toast('Patient Updated Successfully!', { type: 'success' });
            dispatch(clearPatientUpdated());
            setImages([]);
            dispatch(getpatient(patientId));
            return;
        }

        if (error) {
            toast(error, { type: 'error' });
            dispatch(clearError());
            return;
        }

        dispatch(getpatient(patientId));
    }, [isPatientUpdated, error, dispatch, patientId]);


    useEffect(() => {
        if (patient && patient._id) {
            setName(patient.name);
            setPhone(patient.phone);
            setDob(patient.dob ? patient.dob.split('T')[0] : "");
            setAddress(patient.address);
            setRemarks(patient.remarks || "");
            setGender(patient.gender);

            let images = [];
            if (patient.images) {
                patient.images.forEach(image => {
                    images.push(image.image)
                });
            }
            setImagesPreview(images)
        }
    }, [patient])


    return (
        <div className="row g-0">
      <div className="col-12 col-md-2"><Sidebar /></div>
      <div className="col-12 col-md-10 main-content">
                {loading ? <Loader /> :
                    <Fragment>
                        <div className="container my-4">
                            <form onSubmit={submitHandler} className="card shadow p-4" encType='multipart/form-data'>
                                <h3 className="mb-4">Update Patient</h3>

                                <div className="form-group mb-3">
                                    <label htmlFor="name_field">Name</label>
                                    <input
                                        type="text"
                                        id="name_field"
                                        className="form-control"
                                        onChange={(e) => setName(e.target.value.toUpperCase())}
                                        value={name}
                                    />
                                </div>
                                <div className="form-group mb-3">
                                    <label htmlFor="dob_field">
                                        Date of Birth (DOB)
                                        {calculatedAge !== "" && (
                                            <span className="text-success ms-2 fw-bold">
                                                (Calculated Age: {calculatedAge} {calculatedAge === 1 ? 'year' : 'years'})
                                            </span>
                                        )}
                                    </label>
                                    <AppDatePicker
                                        label="Date of Birth (DOB)"
                                        id="dob_field"
                                        value={dob}
                                        onChange={(val) => setDob(val)}
                                    />
                                </div>
                                <div className="form-group mb-3">
                                    <label htmlFor="gender_field">Gender</label>
                                    <select value={gender} onChange={e => setGender(e.target.value)} className="form-control" id="gender_field">
                                        <option value={""}>Select</option>
                                        {genders.map(gender => (
                                            <option key={gender} value={gender}>{gender}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group mb-3">
                                    <label htmlFor="phone_field">Phone</label>
                                    <input
                                        type="text"
                                        id="phone_field"
                                        className="form-control"
                                        onChange={e => setPhone(e.target.value)}
                                        value={phone}
                                    />
                                </div>

                                <div className="form-group mb-3">
                                    <label htmlFor="address_field">Address</label>
                                    <textarea
                                        className="form-control"
                                        id="address_field"
                                        rows="6"
                                        style={{ textTransform: 'uppercase' }}
                                        onChange={e => setAddress(e.target.value.toUpperCase())}
                                        value={address}
                                    ></textarea>
                                </div>

                                <div className="form-group mb-3">
                                    <label htmlFor="remarks_field">Remarks / Quick Patient Information (e.g. Back pain, Diabetes)</label>
                                    <textarea
                                        className="form-control"
                                        id="remarks_field"
                                        rows="3"
                                        placeholder="e.g. Back pain, Lower spine stiffness, Diabetes"
                                        onChange={e => setRemarks(e.target.value)}
                                        value={remarks}
                                    ></textarea>
                                </div>

                                <div className='form-group mb-3'>
                                    <label>Images</label>

                                    <input
                                        type='file'
                                        id='customFile'
                                        className='form-control'
                                        multiple
                                        onChange={onImagesChange}
                                    />

                                    {imagesPreview.length > 0 && (
                                        <div className="mt-2">
                                            <span className="me-2" onClick={clearImagesHandler} style={{ cursor: "pointer" }}>
                                                <i className="fa fa-trash"></i> Clear
                                            </span>
                                            {imagesPreview.map(image => (
                                                <img
                                                    className="mt-2 me-2"
                                                    key={image}
                                                    src={image}
                                                    alt={`Preview`}
                                                    width="55"
                                                    height="52"
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <button
                                    id="update_button"
                                    type="submit"
                                    disabled={loading}
                                    className="btn btn-primary w-100"
                                >
                                    UPDATE
                                </button>

                            </form>
                        </div>
                    </Fragment>
                }
        </div>
        </div>

    )
}



